import { getToken } from './utils/auth';
import { endSession } from './utils/session';
import { refreshAccessToken, setRefreshTransport } from './utils/tokenRefresh';

/**
 * Global 401 yakalayıcı.
 *
 * Talep: "Bir süre hareketsiz kaldıktan sonra menüden bir ekrana tıklayınca istek
 * 401 döndü ve ekran 'veri alınamadı' diyerek asılı kaldı — o durumda login'e
 * atmalı." SessionGuard (#259) yalnız client-side JWT "exp" claim'ine ve 30 sn'lik
 * tick'e bakar; ancak:
 *   - istek tick'ten önce giderse (kullanıcı tam süre dolarken tıklar), ya da
 *   - token sunucu tarafında erken geçersiz kılınırsa (backend restart / iptal),
 * client "exp" hâlâ geçerli sanır ve oturum boşta kalır — ekran hata gösterip
 * login'e atmaz. Bu kapı o boşluğu kapatır.
 *
 * ## Neden artık doğrudan çıkış YAPMIYOR
 * İlk sürüm her 401'de oturumu kapatıyordu. Yenileme mekanizması hiç kullanılmadığı
 * için (bkz. utils/tokenRefresh) bu, "sebepsiz atılma" şikâyetinin ana kaynağıydı:
 * kullanıcı ekrana bakmazken çalışan tek bir arka plan isteğinin 401'i bile bütün
 * oturumu düşürüyordu. Sıra artık şudur:
 *
 *   401 → token'ı yenile → isteği yeni token ile BİR KEZ tekrarla
 *       → yenileme reddedildiyse (oturum gerçekten bitti) çıkış
 *       → yenileme geçici olarak başarısızsa (ağ/429) oturuma DOKUNMA
 *
 * Tekrar bir kezdir: yenilenmiş token ile gelen ikinci 401 artık token sorunu değildir.
 *
 * window.fetch üzerine kurulur; apiBase (installApiBaseUrlSupport) kurulduktan
 * SONRA çağrılmalıdır ki onun rewrite'lı fetch'ini de sarsın.
 */

// Eşzamanlı birden çok istek 401 dönebilir (bir menü tıklaması birkaç çağrı açar);
// yalnız bir kez temizle+yönlendir.
let handling = false;

function getRequestUrl(input) {
  try {
    if (typeof input === 'string') return input;
    if (input instanceof Request) return input.url;
    if (input instanceof URL) return input.toString();
  } catch { /* yoksay */ }
  return '';
}

function isApiRequest(input) {
  const url = getRequestUrl(input);
  if (!url) return false;
  try {
    return new URL(url, window.location.origin).pathname.startsWith('/api/');
  } catch {
    return url.includes('/api/');
  }
}

/**
 * Oturumu 401 sebebiyle kapatır ve olayı teşhis günlüğüne yazar.
 *
 * @param {string} phase Çıkışa hangi adımda karar verildi — 'refresh-rejected'
 *   (sunucu yenilemeyi reddetti, oturum gerçekten bitti) ya da 'retry-401'
 *   (taze token ile de 401 geldi, sorun token değil). Sahada ikisi tamamen
 *   farklı arızalara işaret eder.
 */
function handleUnauthorized(phase, input, init) {
  if (handling) return;
  // Token yoksa zaten çıkış yapılmış ya da hiç giriş yapılmamıştır (ör. hatalı
  // login denemesinin 401'i) — login akışına dokunma.
  if (!getToken()) return;
  handling = true;
  // Temizlik + tam yeniden yükleme utils/session'da; Login ekranı sebebi okuyup
  // "Oturum süreniz doldu" mesajını gösterir.
  //
  // Sebep bilerek 'expired' DEĞİL 'api-401': client "exp" hâlâ geçerliyken sunucunun
  // 401 dönmesi (token iptali, backend restart, saat kayması, başka sekmenin oturumu
  // kapatması) tamamen farklı bir arıza — teşhiste ikisini ayırmak şart.
  endSession('api-401', {
    phase,
    url: getRequestUrl(input),
    method: init?.method ?? (input instanceof Request ? input.method : 'GET'),
  });
}

/** Aynı isteği, Authorization'ı tazeleyerek yeniden gönderir. */
function retryWithToken(fetchFn, input, init, token) {
  // Başlıklar iki kaynaktan gelebilir ve `init` olan, `Request` üzerindekini EZER.
  // Bu yüzden taze token ikisine birden yazılır; yalnız birine yazsaydık istek
  // sessizce eski token ile gidip yine 401 alırdı.
  const base = init?.headers ?? (input instanceof Request ? input.headers : undefined);
  const headers = new Headers(base || {});
  headers.set('Authorization', `Bearer ${token}`);
  const nextInit = { ...init, headers };

  return input instanceof Request
    ? fetchFn(new Request(input, { headers }), nextInit)
    : fetchFn(input, nextInit);
}

export function installAuthInterceptor() {
  if (window.__DATASCOPE_AUTH_INTERCEPTOR_INSTALLED__) return;

  const originalFetch = window.fetch.bind(window);
  // Yenileme isteği BU fetch'ten gider — aşağıdaki sarmalayıcıyı görmez. Böylece
  // yenilemenin kendi 401'i yeni bir yenileme denemesi doğuramaz (sonsuz döngü yok).
  setRefreshTransport(originalFetch);

  window.fetch = async (input, init) => {
    // Gövdeli bir Request nesnesi ilk gönderimde tüketilir; tekrar gerekirse kopyası
    // ÖNCEDEN alınmış olmalı. (Yaygın kullanım string URL + init'tir, orada gerekmez.)
    const replayable = input instanceof Request ? input.clone() : null;

    const response = await originalFetch(input, init);
    if (response.status !== 401 || !isApiRequest(input) || !getToken()) return response;

    const { token, rejected } = await refreshAccessToken();

    if (!token) {
      // Yalnız sunucu "bu oturum bitti" dediğinde çıkış. Geçici engelde (ağ, 429)
      // çağıran normal hata yolunu işletir; oturum ayakta kalır.
      if (rejected) handleUnauthorized('refresh-rejected', input, init);
      return response;
    }

    try {
      const retried = await retryWithToken(originalFetch, replayable ?? input, init, token);
      // Taze token ile de 401 geliyorsa sorun token değildir; oturumu kapat.
      if (retried.status === 401) handleUnauthorized('retry-401', input, init);
      return retried;
    } catch {
      return response; // tekrar ağ hatasına düştü — çağırana ilk yanıtı ver
    }
  };

  window.__DATASCOPE_AUTH_INTERCEPTOR_INSTALLED__ = true;
}
